import type { InsightsPageData, PageProps } from "@/types";
import React, { FC, useRef } from "react";
import { Link } from "../link";
import { ResourcesCta, ResourcesFeaturedPost } from "../resources";
import { PostCard } from "../post-card";
import SplitTextAnimation from "../split-text-animation";
import { useInView } from "@/hooks/reveal-in-view";

export const Insights: FC<PageProps<InsightsPageData>> = (props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });
  const [visibleInsightsCount, setVisibleInsightsCount] = React.useState(6);
  const totalInsightsCount = props.data.insights.length;
  const restElementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      const element = restElementRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        if (
          rect.bottom <= viewportHeight &&
          visibleInsightsCount < totalInsightsCount
        ) {
          setVisibleInsightsCount((prevCount) =>
            Math.min(prevCount + 6, totalInsightsCount)
          );
        }
      }
    };

    const element = restElementRef.current;
    if (element) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (element) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [visibleInsightsCount, totalInsightsCount]);

  return (
    <div>
      <div className="wrapper">
        <div className="resources--links">
          {props.meta.translation.header.resources.children.map(
            (item, itemIdx) => {
              return (
                <Link
                  key={itemIdx}
                  noStyle
                  href={item.link?.uri || "#"}
                  className="resources--link"
                >
                  {item.text}
                </Link>
              );
            }
          )}
        </div>
      </div>
      <ResourcesFeaturedPost
        post={props.data.featuredPost}
        readMore={props.meta.translation.resources.readMore}
      />
      {props.data.insights.length > 0 && (
        <div className="resourcesPage--latest">
          <div ref={ref} className="wrapper">
            <SplitTextAnimation
              htmlElement={
                <h2 className="resourcesPage--title">Latest Insights</h2>
              }
              isInView={isInView}
            />
            <div className="resourcesPage--list">
              {props.data.insights.slice(0, 4).map((insight, index) => {
                return (
                  <PostCard
                    key={index}
                    card={insight}
                    theme="dark"
                    readMore={props.meta.translation.resources.readMore}
                    imagePriority={index < 2}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="wrapper wrapper_lg">
        <ResourcesCta
          title={"Sign up for the newsletter to stay in the know"}
        />
      </div>
      {props.data.insights.length > 4 && (
        <div className="resourcesPage--rest" ref={restElementRef}>
          <div className="wrapper">
            <div className="resourcesPage--list">
              {props.data.insights
                .slice(0, visibleInsightsCount)
                .map((insight, index) => {
                  return (
                    <PostCard
                      key={index}
                      card={insight}
                      theme="dark"
                      readMore={props.meta.translation.resources.readMore}
                    />
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
